import { useMutation } from '@tanstack/react-query';
import { IOperationResultNoValue } from '../../../extViewmodels';
import { IAccountSnapshot } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { ADMIN_ACCOUNT_SNAPSHOT_BY_MONTH_BASE_KEY } from './useAdminAccountSnapshotByMonthQuery';
import { invalidateAdminAccountSnapshotByMonthRenewalSummaryQuery } from './useAdminAccountSnapshotByMonthRenewalSummaryQuery';

interface IUpdateCustomerSuccessSpecialistVariables {
	id: string;
	userId: string;
	justification: string;
}

export function useAdminAccountSnapshotUpdateCustomerSuccessSpecialistMutation() {
	const userSession = useUserSession();
	return useMutation<IAccountSnapshot, IOperationResultNoValue, IUpdateCustomerSuccessSpecialistVariables>({
		mutationFn: ({ id, userId, justification }) =>
			userSession.webServiceHelper.callAsync<IAccountSnapshot>(
				`admin/AccountSnapshot/${encodeURIComponent(id)}/customerSuccessSpecialist`,
				'PATCH',
				{ userId, justification: justification.trim() }
			),
		// Replace the snapshot in every cached byMonth list rather than refetching.
		// Partial-match on a single string element only matches keys whose first item equals
		// this exact string — sibling queries like `byMonth/renewalSummary` use a different
		// first element and are not affected.
		onSuccess: updated => {
			reactQueryClient.setQueriesData<IAccountSnapshot[] | undefined>(
				{ queryKey: [ADMIN_ACCOUNT_SNAPSHOT_BY_MONTH_BASE_KEY] },
				prev => {
					if (!prev) return prev;
					let changed = false;
					const next = prev.map(s => {
						if (s.id === updated.id) {
							changed = true;
							return updated;
						}
						return s;
					});
					return changed ? next : prev;
				}
			);
			// Renewal-summary aggregates may include CSM-segmented counts; refetch so they reflect the change.
			invalidateAdminAccountSnapshotByMonthRenewalSummaryQuery();
		},
	});
}
